import { useState, useEffect, useCallback, useRef } from 'react';
import { isLocalDevHost } from '../utils/apiClient';
import {
  FRONTEND_BUILD_VERSION,
  FRONTEND_BUILD_ID,
  normalizeAppVersion,
  versionsMatch,
} from '../utils/appVersion';
import {
  buildUpdateTargetKey,
  recordUpdateRefreshAttempt,
  readUpdateRefreshAttempt,
  clearUpdateRefreshAttempt,
  shouldBlockAutoRefreshLoop,
} from '../utils/appUpdatePrefs';

const POLL_INTERVAL_MS = 3 * 60 * 1000;
const SW_UPDATE_EVENT = 'app:service-worker-update';

/**
 * The update prompt is driven by hosting's /version.json — the build the CDN
 * would serve on reload — NOT the backend's /api/health version. During a
 * deploy the backend flips minutes before hosting does; comparing against the
 * backend made the old banner nag while a refresh could not possibly help.
 */
async function fetchHostedBuild() {
  // Unique query param defeats any SW/HTTP cache that ignores no-store.
  const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  // Deploys made before version.json existed rewrite it to index.html.
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('json')) return null;
  const data = await res.json();
  const version = normalizeAppVersion(data?.version);
  if (!version) return null;
  return { version, buildId: String(data?.buildId || '').trim() };
}

function isDifferentBuild(hosted) {
  if (!hosted) return false;
  if (hosted.buildId && FRONTEND_BUILD_ID) {
    return hosted.buildId !== FRONTEND_BUILD_ID;
  }
  return !versionsMatch(hosted.version, FRONTEND_BUILD_VERSION);
}

/** Post-reload housekeeping: drop the cache-bust param, settle the attempt record. */
function reconcileAfterReload() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_appRefresh')) {
      url.searchParams.delete('_appRefresh');
      window.history.replaceState({}, '', url.toString());
    }
  } catch (_) {
    // URL cleanup is cosmetic only.
  }

  const attempt = readUpdateRefreshAttempt();
  if (!attempt) return;
  const runningKey = buildUpdateTargetKey({
    version: FRONTEND_BUILD_VERSION,
    buildId: FRONTEND_BUILD_ID,
  });
  // The reload landed on the build we were after — clear the loop guard.
  // A version-only match covers builds from before buildId stamping existed.
  if (
    attempt.targetKey === runningKey ||
    attempt.targetKey.endsWith(`|${FRONTEND_BUILD_VERSION}`)
  ) {
    clearUpdateRefreshAttempt();
  }
}

export function useAppUpdateMonitor() {
  const enabled = !isLocalDevHost();
  const [hostedBuild, setHostedBuild] = useState(null);
  const [swUpdateReady, setSwUpdateReady] = useState(false);
  const checkingRef = useRef(false);
  const hostedBuildRef = useRef(null);

  useEffect(() => {
    if (enabled) reconcileAfterReload();
  }, [enabled]);

  const checkVersion = useCallback(async () => {
    if (!enabled || checkingRef.current) return;
    checkingRef.current = true;
    try {
      const hosted = await fetchHostedBuild();
      if (hosted) {
        hostedBuildRef.current = hosted;
        setHostedBuild(hosted);
        if (!isDifferentBuild(hosted)) {
          setSwUpdateReady(false);
        }
      }
    } catch (_) {
      // Ignore transient network errors during background polling.
    } finally {
      checkingRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    checkVersion();
    const intervalId = window.setInterval(checkVersion, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };

    // A freshly downloaded service worker means a deploy just landed — confirm
    // via version.json so the prompt only shows once a reload will stick.
    const onServiceWorkerUpdate = () => {
      setSwUpdateReady(true);
      checkVersion();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener(SW_UPDATE_EVENT, onServiceWorkerUpdate);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener(SW_UPDATE_EVENT, onServiceWorkerUpdate);
    };
  }, [enabled, checkVersion]);

  const newBuildAvailable = isDifferentBuild(hostedBuild);
  const updateReady = enabled && (newBuildAvailable || swUpdateReady);
  const targetKey = updateReady
    ? buildUpdateTargetKey(
        hostedBuild || { version: FRONTEND_BUILD_VERSION, buildId: 'sw-only' }
      )
    : null;
  const refreshLoopBlocked = updateReady && shouldBlockAutoRefreshLoop(targetKey);

  const refreshApp = useCallback(async () => {
    const hosted = hostedBuildRef.current;
    recordUpdateRefreshAttempt(
      buildUpdateTargetKey(
        hosted || { version: FRONTEND_BUILD_VERSION, buildId: 'sw-only' }
      )
    );

    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }

      const registration = await navigator.serviceWorker?.getRegistration();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      if (registration) {
        await registration.unregister();
      }
    } catch (_) {
      // Best-effort cache bust before reload.
    }

    const url = new URL(window.location.href);
    url.searchParams.set('_appRefresh', String(Date.now()));
    window.location.replace(url.toString());
  }, []);

  return {
    updateReady,
    remoteVersion: hostedBuild?.version || null,
    frontendVersion: FRONTEND_BUILD_VERSION,
    targetKey,
    refreshLoopBlocked,
    refreshApp,
  };
}

export { SW_UPDATE_EVENT };
